import type { BadgeCardProps } from '@pandiafi/components'
import type { TFunction } from 'i18next'

import type {
  CoreParkingArea,
  CoreParkingSpot,
} from '../../../common/validations/contractParkingSpotsSchema'
import {
  fmtCurrency,
  priceForDate,
  priceForToday,
  pricesForDate,
  type PricePeriod,
  QUEUE_EQUIPMENT_DISPLAY_ORDER,
  todayISO,
  normDate,
} from './parkingSpots.helpers'
import {
  ParkingSpotEquipments,
  ParkingSpotEquipment,
} from '../../../common/types'

type BadgeStatus = NonNullable<BadgeCardProps['status']>

type EquipmentKey =
  | 'equipment.covered'
  | 'equipment.ev_charger'
  | 'equipment.garage'
  | 'equipment.heating_outlet'
  | 'equipment.motorbike_spot'
  | 'equipment.yard'
  | 'equipment.building'
  | 'equipment.disabled_spot'

export const equipmentI18nKey: Record<ParkingSpotEquipment, EquipmentKey> = {
  covered: 'equipment.covered',
  ev_charger: 'equipment.ev_charger',
  garage: 'equipment.garage',
  heating_outlet: 'equipment.heating_outlet',
  motorbike_spot: 'equipment.motorbike_spot',
  yard: 'equipment.yard',
  building: 'equipment.building',
  disabled_spot: 'equipment.disabled_spot',
} as const

export const ALL_EQUIPMENTS: ReadonlyArray<ParkingSpotEquipment> =
  ParkingSpotEquipments

export const equipmentLabel = (
  e: ParkingSpotEquipment,
  t: TFunction<'parking'>
) => t(equipmentI18nKey[e])

export const equipmentOptions = (
  t: TFunction<'parking'>,
  allowed: ReadonlyArray<ParkingSpotEquipment> = ALL_EQUIPMENTS
) =>
  QUEUE_EQUIPMENT_DISPLAY_ORDER.filter((value) => allowed.includes(value)).map(
    (value) => ({
      value,
      label: equipmentLabel(value, t),
    })
  )

export const equipmentLabels = (
  values: ReadonlyArray<ParkingSpotEquipment>,
  t: TFunction<'parking'>
) =>
  QUEUE_EQUIPMENT_DISPLAY_ORDER.filter((value) => values.includes(value)).map(
    (value) => equipmentLabel(value, t)
  )

export const formatList = (labels: string[]) => {
  if (labels.length <= 1) return labels.join('')
  return `${labels.join(', ')}`
}

const statusI18nKey: Record<
  BadgeStatus,
  | 'legend.available'
  | 'legend.reservedOrInactive'
  | 'legend.mine'
  | 'legend.mineUpcoming'
  | 'legend.notInUse'
  | 'legend.ending'
  | 'legend.starting'
> = {
  default: 'legend.available',
  disabled: 'legend.reservedOrInactive',
  active: 'legend.mine',
  upcoming: 'legend.mineUpcoming',
  'not-in-use': 'legend.notInUse',
  ending: 'legend.ending',
  starting: 'legend.starting',
} as const

const has = <T>(arr: readonly T[] | T[] | undefined, val: T): arr is T[] =>
  // eslint-disable-next-line no-object-comparison/array-includes-object
  Array.isArray(arr) && arr.includes(val)

export const spotStatusLabel = (
  status: BadgeCardProps['status'] | undefined,
  t: TFunction<'parking'>
): string | undefined => {
  if (!status) return undefined
  const key = statusI18nKey[status as BadgeStatus]
  return key ? t(key) : String(status)
}

export const spotDisplayId = (spot: CoreParkingSpot): string =>
  spot.locationDetails ?? String(spot.rentableParkingSpotId ?? '')

export const spotEquipment = (
  spot: CoreParkingSpot
): ParkingSpotEquipment[] => {
  const raw = spot.equipment ?? []
  return raw.filter(
    (e: unknown): e is ParkingSpotEquipment =>
      typeof e === 'string' && e in equipmentI18nKey
  )
}

export const spotEquipmentLabel = (
  spot: CoreParkingSpot,
  t: TFunction<'parking', undefined>
): string | undefined => {
  const eq = spotEquipment(spot)
  if (eq.length === 0) return undefined
  return eq.map((e) => t(equipmentI18nKey[e])).join(', ')
}

export const spotTitle = (spot: CoreParkingSpot): string => {
  return `${spot.additionalDetails} ${spot.locationDetails}`
}

export const isDisabledSpot = (spot: CoreParkingSpot): boolean => {
  return spotEquipment(spot).includes('disabled_spot')
}

const getEquipmentIcon = (
  equipment: ParkingSpotEquipment[]
): BadgeCardProps['icon'] | null => {
  if (has(equipment, 'ev_charger')) return 'electric-charging'
  if (has(equipment, 'heating_outlet')) return 'electricity'
  return null
}

const getTypeIcon = (
  equipment: ParkingSpotEquipment[]
): BadgeCardProps['icon'] | null => {
  const isDisabled = has(equipment, 'disabled_spot')
  const isMotorbike = has(equipment, 'motorbike_spot')

  let prefix: 'inva-' | 'motorbike-' | '' = ''
  if (isDisabled) prefix = 'inva-'
  else if (isMotorbike) prefix = 'motorbike-'

  if (has(equipment, 'covered'))
    return `${prefix}shelter` as BadgeCardProps['icon']
  if (has(equipment, 'garage'))
    return `${prefix}garage` as BadgeCardProps['icon']
  if (has(equipment, 'building'))
    return `${prefix}hall` as BadgeCardProps['icon']

  if (isDisabled) return 'inva'
  if (isMotorbike) return 'motorbike'
  return null
}

export const spotIcons = (
  spot: CoreParkingSpot
): {
  primary: BadgeCardProps['icon']
  secondary?: BadgeCardProps['icon']
} => {
  const eq = spotEquipment(spot)
  const equipmentIcon = getEquipmentIcon(eq)
  const typeIcon = getTypeIcon(eq)

  const primary = (equipmentIcon ||
    typeIcon ||
    'empty') as BadgeCardProps['icon']
  const secondary =
    typeIcon && typeIcon !== primary
      ? (typeIcon as BadgeCardProps['icon'])
      : undefined

  return { primary, secondary }
}

export const spotPriceFor = (
  spot: CoreParkingSpot,
  atISO?: string | null
): number | undefined => {
  const pList = (spot.prices ?? []) as PricePeriod[] | undefined
  return priceForDate(pList, atISO ?? null) ?? priceForToday(pList)
}

export const spotPricesFor = (
  spot: CoreParkingSpot,
  atISO?: string | null
): PricePeriod[] =>
  pricesForDate((spot.prices ?? []) as PricePeriod[], atISO ?? null)

export const paymentTypeName = (
  price: PricePeriod,
  language: string
): string => {
  if (language.startsWith('sv')) return price.nameOnBillSv ?? price.nameOnBill
  if (language.startsWith('en')) return price.nameOnBillEn ?? price.nameOnBill
  return price.nameOnBill
}

export const spotPriceLabel = (
  spot: CoreParkingSpot,
  locale: string,
  atISO?: string | null
): string | undefined => {
  const price = spotPriceFor(spot, atISO)
  return typeof price === 'number' ? fmtCurrency(price, locale) : undefined
}

export const areaAddressLabel = (area: CoreParkingArea): string => {
  const addr = area.location.address
  return `${addr.streetName} ${addr.streetNumber}, ${addr.city}`
}

export const areaPriceRange = (
  area: CoreParkingArea,
  atISO?: string | null
): { min?: number; max?: number } => {
  const prices = (area.parkingSpots ?? [])
    .map((s) => spotPriceFor(s, atISO))
    .filter((n): n is number => typeof n === 'number')

  if (prices.length === 0) return {}

  const { min, max } = prices.reduce(
    (acc, p) => ({
      min: Math.min(acc.min, p),
      max: Math.max(acc.max, p),
    }),
    { min: Number.POSITIVE_INFINITY, max: Number.NEGATIVE_INFINITY }
  )

  return { min, max }
}

export const areaPriceLabel = (
  area: CoreParkingArea,
  locale: string,
  t: TFunction<'parking', undefined>,
  atISO?: string | null
): string => {
  const { min, max } = areaPriceRange(area, atISO)
  if (typeof min !== 'number' || !Number.isFinite(min)) return ''

  const amount = fmtCurrency(min, locale)
  return min === max
    ? t('price.perMonth', { amount })
    : t('price.fromPerMonth', { amount })
}

export const findActiveReservation = (
  spot: CoreParkingSpot,
  today = todayISO()
) =>
  (spot.reservations ?? []).find((r) => {
    const s = normDate(r.startDate) ?? '0000-01-01'
    const e = normDate(r.endDate) ?? '9999-12-31'
    return s <= today && today <= e
  })

export const findFutureReservation = (
  spot: CoreParkingSpot,
  today = todayISO()
) =>
  (spot.reservations ?? [])
    .filter((r) => {
      const s = normDate(r.startDate)
      return !!s && s > today
    })
    .sort((a, b) => {
      const as = normDate(a.startDate) ?? '9999-12-31'
      const bs = normDate(b.startDate) ?? '9999-12-31'
      return as.localeCompare(bs)
    })[0]

export const findPreviousReservation = (
  spot: CoreParkingSpot,
  today = todayISO()
) =>
  (spot.reservations ?? [])
    .filter((r) => {
      const e = normDate(r.endDate)
      return !!e && e < today
    })
    .sort((a, b) => {
      const ae = normDate(a.endDate) ?? '0000-01-01'
      const be = normDate(b.endDate) ?? '0000-01-01'
      return be.localeCompare(ae)
    })[0]

export const formatDate = (iso: string, locale = 'fi-FI') => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(locale)
}
